/**
 * Generated Senders Preview – select, Apply, Use & Hide, Dismiss, Regenerate
 * For Media Devoted | Created By Ciro Autiero
 */

import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { Check, X, Sparkles, RefreshCw, EyeOff } from 'lucide-react';

import { Button, LoadingSpinner } from '@/components/ui';

import { PreviewTextItem } from './PreviewTextItem';

interface GeneratedSendersPreviewProps {
  senders: string[];
  onSave: (selected: string[]) => void;
  onSaveAndHide?: (selected: string[]) => void;
  onDismiss: () => void;
  onRegenerate?: (unselectedIndices: number[]) => void;
  isRegenerating?: boolean;
  fillHeight?: boolean;
  /** When true, uses smaller height to leave room for sibling content (e.g. prepared drop). */
  compact?: boolean;
}

export function GeneratedSendersPreview({
  senders,
  onSave,
  onSaveAndHide,
  onDismiss,
  onRegenerate,
  isRegenerating,
  fillHeight = false,
  compact = false,
}: GeneratedSendersPreviewProps) {
  const [selected, setSelected] = useState<Set<number>>(() =>
    new Set(senders.map((_, i) => i)),
  );
  const prevSendersRef = useRef<string[]>(senders);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = containerRef.current as HTMLElement | null;
    if (senders.length > 0 && el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [senders.length]);

  useEffect(() => {
    const prev = prevSendersRef.current;
    if (prev.length === senders.length) {
      const changedIndices: number[] = [];
      for (let i = 0; i < senders.length; i++) {
        if (prev[i] !== senders[i]) changedIndices.push(i);
      }
      if (changedIndices.length > 0) {
        setSelected((s) => {
          const next = new Set(s);
          for (const idx of changedIndices) next.add(idx);
          return next;
        });
      }
    }
    prevSendersRef.current = senders;
  }, [senders]);

  const toggleItem = useCallback((idx: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }, []);

  const allSelected = useMemo(
    () => selected.size === senders.length,
    [selected.size, senders.length],
  );
  const unselectedCount = senders.length - selected.size;

  const toggleAll = useCallback(() => {
    setSelected(allSelected ? new Set() : new Set(senders.map((_, i) => i)));
  }, [allSelected, senders]);

  const handleSave = useCallback(() => {
    const chosen = senders.filter((_, i) => selected.has(i));
    if (chosen.length > 0) onSave(chosen);
  }, [senders, selected, onSave]);

  const handleRegenerate = useCallback(() => {
    if (unselectedCount === 0 || !onRegenerate) return;
    const indices = senders.map((_, i) => i).filter((i) => !selected.has(i));
    onRegenerate(indices);
  }, [unselectedCount, onRegenerate, senders, selected]);

  const handleSaveAndHide = useCallback(() => {
    const chosen = senders.filter((_, i) => selected.has(i));
    if (chosen.length > 0 && onSaveAndHide) onSaveAndHide(chosen);
  }, [senders, selected, onSaveAndHide]);

  const showRegen = onRegenerate && unselectedCount > 0;
  const containerClass = fillHeight
    ? 'flex min-h-0 flex-1 flex-col overflow-hidden border-b border-border'
    : compact
      ? 'flex max-h-[min(360px,45vh)] min-h-[100px] flex-col overflow-hidden border-b border-border sm:min-h-[120px]'
      : 'flex max-h-[min(480px,60vh)] min-h-[100px] flex-col overflow-hidden border-b border-border sm:min-h-[120px] md:min-h-[140px]';

  const content = (
    <div ref={containerRef} className={containerClass}>
      <div className="sticky top-0 z-20 flex shrink-0 items-center justify-between rounded-t-lg border-b border-border bg-background px-3 py-2 shadow-sm backdrop-blur-sm">
        <span className="flex items-center gap-1 text-[10px] font-semibold uppercase text-muted-foreground">
          <Sparkles className="h-3 w-3 text-primary" />
          Generated ({selected.size}/{senders.length})
        </span>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-6 text-[10px]"
            onClick={toggleAll}
          >
            {allSelected ? 'Deselect all' : 'Select all'}
          </Button>
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onDismiss}>
            <X className="h-3 w-3" />
          </Button>
        </div>
      </div>
      <div className="flex min-h-[60px] min-w-0 flex-1 flex-row flex-wrap content-start gap-1 overflow-y-auto px-2 py-1.5 scrollbar-thin sm:min-h-[80px]">
        {senders.map((s, i) => (
          <PreviewTextItem
            key={`${i}-${s}`}
            text={s}
            index={i}
            isSelected={selected.has(i)}
            onToggle={toggleItem}
            chip
          />
        ))}
      </div>
      <div className="z-20 shrink-0 space-y-1.5 rounded-b-lg border-t border-border bg-background px-3 pt-2 pb-3 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]">
        <div className="flex gap-1.5">
          <Button
            variant="outline"
            size="sm"
            className="h-7 flex-1 gap-1 text-xs"
            onClick={handleSave}
            disabled={selected.size === 0}
          >
            <Check className="h-3 w-3" />
            Save {selected.size}
          </Button>
          {showRegen && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 flex-1 gap-1 text-xs"
              onClick={handleRegenerate}
              disabled={isRegenerating}
            >
              {isRegenerating ? (
                <LoadingSpinner className="h-3 w-3" />
              ) : (
                <RefreshCw className="h-3 w-3" />
              )}
              Regen {unselectedCount}
            </Button>
          )}
          {onSaveAndHide && (
            <Button
              size="sm"
              className="h-7 flex-1 gap-1 text-xs"
              onClick={handleSaveAndHide}
              disabled={selected.size === 0}
            >
              <EyeOff className="h-3 w-3" />
              Use & Hide
            </Button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div
      className={fillHeight ? 'flex min-h-0 flex-1 flex-col overflow-hidden w-full' : 'w-full'}
    >
      {content}
    </div>
  );
}
